
















import Vue from "vue";
import Partner from "@/modules/partner/models/Partner";
import PartnerApi from "@/modules/partner/services/PartnerApi";
import configuration from "@/models/Configuration";
import { t } from "@/plugins/i18n";

interface InfoRow {
  label: string;
  value: string;
  test?: string;
}

interface Data {
  partner: Partner | null;
  extCodes: string[];
  loading: boolean;
}

interface Methods {
  load(): void;
  joinParts(parts: (string | number | null | undefined)[]): string;
}

interface Computed {
  rows: InfoRow[];
  invoiceAddress: string;
  corAddress: string;
}

interface Props {
  partnerId: number;
}

/**
 * Kompakt, csak olvasható partner alapadat lista (a PartnerEditPage "Partner alap adatai" blokkjának kicsinyített változata), keret nélkül.
 * A partnerId alapján maga tölti be a partnert.
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    partnerId: { type: Number, required: true },
  },

  data() {
    return {
      partner: null,
      extCodes: [],
      loading: false,
    };
  },

  computed: {
    invoiceAddress(): string {
      const p = this.partner;
      if (!p) return "";
      if (configuration.partner.isExtendedPartnerInfoEnabled) {
        return this.joinParts([
          p.postCode,
          p.invoiceCity + ",",
          p.invStreet,
          p.invStreetTypeName,
          p.invStreetNum,
          p.invBuilding,
          p.invFloor,
          p.invDoor,
        ]);
      }
      return this.joinParts([p.postCode, p.invoiceCity + ",", p.invoiceAddress]);
    },

    corAddress(): string {
      const p = this.partner;
      if (!p) return "";
      if (configuration.partner.isExtendedPartnerInfoEnabled) {
        return this.joinParts([
          p.corPostCode,
          p.corCity + ",",
          p.corStreet,
          p.corStreetTypeName,
          p.corStreetNum,
          p.corBuilding,
          p.corFloor,
          p.corDoor,
        ]);
      }
      return this.joinParts([p.corPostCode, p.corCity + ",", p.corAddress]);
    },

    rows(): InfoRow[] {
      const p = this.partner;
      if (!p) return [];
      return [
        { label: t("PartnerID"), value: String(p.id), test: "info-id" },
        { label: t("PartnerNeve"), value: p.name, test: "info-name" },
        { label: t("Partner_PartnerNevKiegeszitese"), value: p.nameExt },
        { label: t("Partner_RovidNev"), value: p.nicName },
        { label: t("LevelezesiNev"), value: p.corName },
        { label: t("VallalkozasiForma"), value: p.companyForm },
        { label: t("Belfoldi"), value: p.native ? "Igen" : "Nem" },
        { label: t("SzamlazasiCim"), value: this.invoiceAddress },
        { label: t("LevelezesiCim"), value: this.corAddress },
        { label: t("Orszag"), value: p.countryName },
        { label: t("Telefon"), value: p.phoneNumber },
        { label: t("Partner_Mobil"), value: p.mobileNumber },
        { label: "E-mail", value: p.email },
        { label: t("Partner_UgyfelStatusz"), value: p.status, test: "info-status" },
        { label: t("PartnerTipus"), value: p.partnerType },
        { label: t("FizetesiMod"), value: p.payMode },
        { label: t("Partner_FizetesiMinosites"), value: p.payClass },
        { label: t("Partner_UgyfelhezTartozoPenznem"), value: p.defCurrency },
        { label: t("FizetesiHataridoNap"), value: p.payTerm != null ? String(p.payTerm) : "" },
        { label: t("Partner_BelfoldiAdoszam"), value: p.taxAccount },
        { label: t("KozossegiAdoszam"), value: p.euTaxAccount },
        { label: t("Partner_KulsoAzonositok"), value: this.extCodes.join(", "), test: "info-ext-codes" },
      ];
    },
  },

  watch: {
    partnerId() {
      this.load();
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      if (!this.partnerId || this.partnerId <= 1) {
        this.partner = null;
        this.extCodes = [];
        this.$emit("loaded");
        return;
      }
      const requestedId = this.partnerId;
      this.loading = true;
      try {
        const [partner, extCodes] = await Promise.all([
          PartnerApi.get(requestedId),
          PartnerApi.getExtCodes(requestedId).catch(() => [] as string[]),
        ]);
        // ha közben másik partnert választottak, a régi választ eldobjuk
        if (requestedId === this.partnerId) {
          this.partner = partner;
          this.extCodes = extCodes;
        }
      } catch {
        this.partner = null;
        this.extCodes = [];
      } finally {
        this.loading = false;
        // a szülő oldal erre várhat (pl. előtöltésnél a teljes oldalas töltés eltüntetéséhez)
        this.$emit("loaded");
      }
    },

    joinParts(parts: (string | number | null | undefined)[]): string {
      return parts
        .map((x) => (x == null ? "" : String(x).trim()))
        .filter((x) => x !== "" && x !== ",")
        .join(" ")
        .replace(/\s+,/g, ",");
    },
  },
});
